import React, { useEffect, useState } from "react";
import "../../assets/styles/card.css";
import "../../assets/styles/toggle.css";
import BASE_URL from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function DeviceSettingsCard({ customerId = null }) {
  const isAdminMode = Boolean(customerId);
  const [auVol, setAuVol] = useState(75);
  const [detectionRange, setDetectionRange] = useState(1600);
  const [buzzerLevel, setBuzzerLevel] = useState(3);
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [studyEdition, setStudyEdition] = useState(false);
  const [studyEditionSaving, setStudyEditionSaving] = useState(false);
  const [deviceUid, setDeviceUid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem("jwt_token");

        if (isAdminMode) {
          const deviceRes = await fetch(`${BASE_URL}/api/cust_devices/${customerId}/devices`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (deviceRes.ok) {
            const deviceData = await deviceRes.json();
            const device = Array.isArray(deviceData) ? deviceData[0] : deviceData;
            if (device?.uid) {
              setDeviceUid(device.uid);
              if (typeof device.update_latest === "boolean") {
                setAutoUpdate(device.update_latest);
              }
              if (typeof device.study_edition === "boolean") {
                setStudyEdition(device.study_edition);
              }
            }
          }
        } else {
          const deviceRes = await fetch(`${BASE_URL}/api/get_cust_devices/`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
          });

          if (deviceRes.ok) {
            const deviceData = await deviceRes.json();
            const device = Array.isArray(deviceData) ? deviceData[0] : deviceData;
            if (device?.uid) {
              setDeviceUid(device.uid);
              if (typeof device.update_latest === "boolean") {
                setAutoUpdate(device.update_latest);
              }
            }
          }
        }

        const settingsUrl = isAdminMode
          ? `${BASE_URL}/api/preferences/admin/device-settings/?cust_id=${customerId}`
          : `${BASE_URL}/api/preferences/device-settings/`;
        const res = await fetch(settingsUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (res.ok) {
          const data = await res.json();
          setAuVol(data.au_vol ?? 75);
          setDetectionRange(data.detection_range ?? 1600);
          setBuzzerLevel(data.buzzer_level ?? 3);
        } else {
          console.error("Failed to fetch device settings");
        }
      } catch (error) {
        console.error("Error fetching device settings:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [customerId, isAdminMode]);

  const handleAutoUpdateToggle = async () => {
    if (!deviceUid) return;
    const newValue = !autoUpdate;
    setAutoUpdate(newValue);
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BASE_URL}/api/devices/${deviceUid}/auto-update/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ update_latest: newValue }),
      });

      if (res.ok) {
        toast.success(`Auto-update ${newValue ? "enabled" : "disabled"}`);
      } else {
        setAutoUpdate(!newValue);
        toast.error("Failed to update auto-update setting");
      }
    } catch (error) {
      setAutoUpdate(!newValue);
      console.error("Error updating auto-update setting:", error);
      toast.error("Error updating auto-update setting");
    }
  };

  const handleStudyEditionToggle = async () => {
    // Study Edition (DSA) is admin-only: this device field is not exposed
    // via the customer-facing preferences API, so the toggle only renders
    // in admin mode (see isAdminMode check below) and writes straight to
    // the admin device-update endpoint, mirroring auto-update above.
    if (!deviceUid) return;
    const newValue = !studyEdition;
    setStudyEdition(newValue);
    setStudyEditionSaving(true);
    try {
      const token = localStorage.getItem("jwt_token");
      const res = await fetch(`${BASE_URL}/api/admin/update-device/${deviceUid}/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ study_edition: newValue }),
      });

      if (res.ok) {
        toast.success(`Study Edition mode ${newValue ? "enabled" : "disabled"}`);
      } else {
        setStudyEdition(!newValue);
        toast.error("Failed to update Study Edition setting");
      }
    } catch (error) {
      setStudyEdition(!newValue);
      console.error("Error updating Study Edition setting:", error);
      toast.error("Error updating Study Edition setting");
    } finally {
      setStudyEditionSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("jwt_token");
      const url = isAdminMode
        ? `${BASE_URL}/api/preferences/admin/device-settings/?cust_id=${customerId}`
        : `${BASE_URL}/api/preferences/device-settings/`;
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          au_vol: auVol,
          detection_range: detectionRange,
          buzzer_level: buzzerLevel,
        }),
      });

      if (res.ok) {
        toast.success("Device settings updated!");
      } else {
        toast.error("Failed to update device settings");
      }
    } catch (error) {
      console.error("Error updating device settings:", error);
      toast.error("Error updating device settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      <div className="pref-card-container">
        <h2 className="pref-card-title">Device Settings</h2>
        {isAdminMode && (
          <p className="pref-card-description">
            Configure this customer&apos;s device audio and detection settings.
          </p>
        )}

        {loading ? (
          <p className="pref-card-loading">Loading...</p>
        ) : (
          <>
            {/* Audio Volume */}
            <div className="pref-row device-setting-row">
              <div className="device-setting-label-group">
                <span className="pref-card-mode">Audio Volume</span>
                <span className="device-setting-value">{auVol}%</span>
              </div>
              <input
                type="range"
                className="device-setting-slider"
                min={0}
                max={100}
                step={1}
                value={auVol}
                onChange={(e) => setAuVol(Number(e.target.value))}
                aria-label="Audio Volume"
                aria-valuetext={`${auVol} percent`}
              />
            </div>

            {/* Detection Range */}
            <div className="pref-row">
              <span className="pref-card-mode">Detection Range</span>
              <select
                className="device-setting-select"
                value={detectionRange}
                onChange={(e) => setDetectionRange(Number(e.target.value))}
                aria-label="Detection Range"
              >
                <option value={1000}>Low - 1000 mm</option>
                <option value={1600}>Medium - 1600 mm</option>
                <option value={2200}>High - 2200 mm</option>
              </select>
            </div>

            {/* Buzzer Level */}
            <div className="pref-row">
              <span className="pref-card-mode">Buzzer Level</span>
              <select
                className="device-setting-select"
                value={buzzerLevel}
                onChange={(e) => setBuzzerLevel(Number(e.target.value))}
                aria-label="Buzzer Level"
              >
                <option value={1}>1 - Very Low</option>
                <option value={2}>2 - Low</option>
                <option value={3}>3 - Medium</option>
                <option value={4}>4 - High</option>
                <option value={5}>5 - Very High</option>
              </select>
            </div>

            {/* Auto Update */}
            <div className="pref-row">
              <span className="pref-card-mode">Auto Update</span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  checked={autoUpdate}
                  onChange={handleAutoUpdateToggle}
                  disabled={!deviceUid}
                  aria-label="Auto Update"
                />
                <span className="pref-slider small"></span>
              </label>
            </div>

            {/* Study Edition (DSA) mode -- admin only */}
            {isAdminMode && (
              <div className="pref-row">
                <div className="device-setting-label-group">
                  <span className="pref-card-mode">Study Edition (DSA) Mode</span>
                  <span className="device-setting-value" style={{ fontWeight: "normal", fontSize: "0.85em" }}>
                    Disables scene description, barcode, navigation, bus/train times, web search &amp; facial recognition
                  </span>
                </div>
                <label className="pref-toggle-switch small">
                  <input
                    type="checkbox"
                    checked={studyEdition}
                    onChange={handleStudyEditionToggle}
                    disabled={!deviceUid || studyEditionSaving}
                    aria-label="Study Edition (DSA) Mode"
                  />
                  <span className="pref-slider small"></span>
                </label>
              </div>
            )}

            {/* Save Button */}
            <div className="device-setting-save-row">
              <button
                className="device-setting-save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default DeviceSettingsCard;
